import * as React from "react";
import {
  ArrowLeft, TrendingUp, Search, Loader2, ChevronRight, AlertTriangle,
  DollarSign, MousePointer, Eye, Target, BarChart3, HelpCircle, ArrowUpDown
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Cell, PieChart, Pie
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { trackActivity } from "@/utils/trackActivity";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HelpTip = ({ text, side = "top" }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild>
      <HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline-block ml-1" />
    </TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-slate-900 text-slate-100 px-3 py-2">{text}</TooltipContent>
  </Tooltip>
);

const fmt = (n) => n != null ? Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "0";
const fmtM = (n) => {
  if (!n) return "$0";
  if (n >= 1000000) return "$" + (n/1000000).toFixed(1) + "M";
  if (n >= 1000) return "$" + (n/1000).toFixed(1) + "K";
  return "$" + n.toFixed(0);
};

const PLATFORM_COLORS = {
  google_ads: "bg-blue-100 text-blue-700",
  facebook_ads: "bg-indigo-100 text-indigo-700",
  tiktok_ads: "bg-slate-800 text-white",
  dv360: "bg-green-100 text-green-700",
};

// === CLIENT LIST ===
const ClientList = ({ onSelect, days, setDays }) => {
  const [clients, setClients] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/campaign-performance/clients?days=${days}`, { withCredentials: true });
        setClients(res.data.clients || []);
        trackActivity("view_module", "campaign_performance");
      } catch (e) { toast.error("Error al cargar clientes"); }
      finally { setIsLoading(false); }
    })();
  }, [days]);

  const filtered = clients.filter(c => (c.cliente || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Performance por Cliente</h2>
          <p className="text-slate-500 text-sm mt-1">{clients.length} clientes activos en los ultimos {days} dias</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Ultimos 7 dias</SelectItem>
            <SelectItem value="14">Ultimos 14 dias</SelectItem>
            <SelectItem value="30">Ultimos 30 dias</SelectItem>
            <SelectItem value="60">Ultimos 60 dias</SelectItem>
            <SelectItem value="90">Ultimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => (
            <Card key={c.cliente} className="cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5" onClick={() => { trackActivity("select_client", "campaign_performance", { cliente: c.cliente }); onSelect(c.cliente); }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-800">{c.cliente}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{c.campaigns} campanas · {c.platforms} plataformas</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300" />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-slate-400">Spend</p>
                    <p className="font-bold text-slate-800">{fmtM(c.spend)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">ROAS</p>
                    <p className={`font-bold ${c.roas >= 3 ? "text-emerald-600" : c.roas >= 1 ? "text-amber-600" : "text-red-600"}`}>{c.roas}x</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Conversiones</p>
                    <p className="font-bold text-slate-700">{fmt(c.conversions)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">CPA</p>
                    <p className="font-bold text-slate-700">${fmt(c.cpa)}</p>
                  </div>
                </div>
                {c.unclassified_pct >= 30 && (
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 rounded px-2 py-1">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                    <span>{c.unclassified_camps} campanas sin clasificar ({c.unclassified_pct}%) — puede afectar las metricas</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-slate-400 col-span-3 text-center py-8">No se encontraron clientes</p>}
        </div>
      )}
    </div>
  );
};

// === DASHBOARD ===
const Dashboard = ({ cliente, days, setDays, onBack }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [sortKey, setSortKey] = React.useState("spend");
  const [sortDir, setSortDir] = React.useState("desc");

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/campaign-performance/dashboard/${encodeURIComponent(cliente)}?days=${days}`, { withCredentials: true });
        setData(res.data);
      } catch (e) { toast.error("Error al cargar dashboard"); }
      finally { setIsLoading(false); }
    })();
  }, [cliente, days]);

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const { summary: s, by_platform, by_objective, campaigns, daily_trend } = data;

  const OBJ_STYLES = {
    awareness: { color: "bg-purple-100 text-purple-700", label: "Awareness", kpi: "CPM" },
    consideration: { color: "bg-blue-100 text-blue-700", label: "Consideration", kpi: "CPC/CTR" },
    conversion: { color: "bg-emerald-100 text-emerald-700", label: "Conversion", kpi: "ROAS/CPA" },
    unclassified: { color: "bg-slate-100 text-slate-500", label: "Sin clasificar", kpi: "-" },
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const sorted = [...campaigns].sort((a, b) => {
    const va = a[sortKey] || 0, vb = b[sortKey] || 0;
    return sortDir === "desc" ? vb - va : va - vb;
  });

  const SortHeader = ({ k, children, className = "" }) => (
    <TableHead className={`cursor-pointer select-none hover:bg-slate-50 ${className}`} onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-0.5">
        {children}
        {sortKey === k && <ArrowUpDown className="h-3 w-3 text-indigo-500" />}
      </span>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Clientes</Button>
          <div className="h-5 w-px bg-slate-200" />
          <h2 className="text-xl font-bold text-slate-800">{cliente}</h2>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="14">14 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
            <SelectItem value="60">60 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: "Spend", value: fmtM(s.spend), icon: DollarSign, color: "text-slate-700", tip: "Inversion total en el periodo" },
          { label: "ROAS", value: `${s.roas}x`, icon: TrendingUp, color: s.roas >= 3 ? "text-emerald-600" : s.roas >= 1 ? "text-amber-600" : "text-red-600", tip: "Return on Ad Spend: revenue / spend" },
          { label: "CPA", value: `$${fmt(s.cpa)}`, icon: Target, color: "text-slate-700", tip: "Costo por conversion" },
          { label: "Conv.", value: fmt(s.conversions), icon: Target, color: "text-slate-700", tip: "Total de conversiones" },
          { label: "CTR", value: `${s.ctr}%`, icon: MousePointer, color: "text-slate-700", tip: "Click-through rate: clicks / impressions" },
          { label: "Campanas", value: s.campaigns, icon: BarChart3, color: "text-indigo-600", tip: "Campanas activas con gasto en el periodo" },
        ].map((kpi, i) => (
          <Card key={i}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-slate-400 inline-flex items-center">{kpi.label}<HelpTip text={kpi.tip} /></p>
                <kpi.icon className="h-3.5 w-3.5 text-slate-300" />
              </div>
              <p className={`text-lg font-bold mt-0.5 ${kpi.color}`}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* By Objective */}
      {by_objective && Object.keys(by_objective).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Por Objetivo
              <HelpTip text="Clasificacion automatica basada en el nombre de la campana. Cada objetivo se evalua con KPIs diferentes: Awareness=CPM, Consideration=CPC/CTR, Conversion=ROAS/CPA." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(by_objective).map(([obj, d]) => {
                const st = OBJ_STYLES[obj] || OBJ_STYLES.unclassified;
                return (
                  <div key={obj} className="p-3 rounded-lg border bg-slate-50">
                    <Badge className={`text-[10px] mb-2 ${st.color}`}>{st.label}</Badge>
                    <p className="text-xs text-slate-400 mb-1">{d.campaigns} campanas · {fmtM(d.spend)}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {obj === 'awareness' && (<>
                        <div><p className="text-slate-400">CPM</p><p className="font-bold">${fmt(d.cpm)}</p></div>
                        <div><p className="text-slate-400">Impressions</p><p className="font-bold">{(d.impressions/1000000).toFixed(1)}M</p></div>
                      </>)}
                      {obj === 'consideration' && (<>
                        <div><p className="text-slate-400">CPC</p><p className="font-bold">${fmt(d.cpc)}</p></div>
                        <div><p className="text-slate-400">CTR</p><p className="font-bold">{d.ctr}%</p></div>
                      </>)}
                      {(obj === 'conversion' || obj === 'unclassified') && (<>
                        <div><p className="text-slate-400">ROAS</p><p className={`font-bold ${d.roas >= 3 ? "text-emerald-600" : d.roas >= 1 ? "text-amber-600" : "text-red-600"}`}>{d.roas}x</p></div>
                        <div><p className="text-slate-400">CPA</p><p className="font-bold">${fmt(d.cpa)}</p></div>
                      </>)}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Platform */}
      {by_platform.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Por Plataforma</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {by_platform.map(p => (
                <div key={p.platform} className="p-3 rounded-lg border bg-slate-50">
                  <Badge className={`text-[10px] mb-2 ${PLATFORM_COLORS[p.platform] || "bg-slate-100 text-slate-600"}`}>{p.platform}</Badge>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><p className="text-slate-400">Spend</p><p className="font-bold">{fmtM(p.spend)}</p></div>
                    <div><p className="text-slate-400">ROAS</p><p className={`font-bold ${p.roas >= 3 ? "text-emerald-600" : p.roas >= 1 ? "text-amber-600" : "text-red-600"}`}>{p.roas}x</p></div>
                    <div><p className="text-slate-400">Conv.</p><p className="font-bold">{fmt(p.conversions)}</p></div>
                    <div><p className="text-slate-400">CPA</p><p className="font-bold">${fmt(p.cpa)}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* 1. Area: Daily Spend Trend */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Tendencia Diaria
              <HelpTip text="Gasto diario y conversiones en el periodo seleccionado." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={daily_trend} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                <defs>
                  <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? (v/1000).toFixed(0)+"K" : v} />
                <RTooltip formatter={(v, name) => [name === "spend" ? "$"+Number(v).toLocaleString() : Number(v).toLocaleString(), name === "spend" ? "Gasto" : "Conversiones"]} labelFormatter={(l) => l} />
                <Area type="monotone" dataKey="spend" stroke="#6366f1" fill="url(#spendGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="conversions" stroke="#10b981" fill="none" strokeWidth={1.5} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* 2. Donut: Spend by Objective */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Gasto por Objetivo
              <HelpTip text="Distribucion del presupuesto segun el objetivo de la campana." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const OBJ_COLORS = { Awareness: "#a855f7", Consideration: "#3b82f6", Conversion: "#10b981", "Sin clasificar": "#94a3b8" };
              const OBJ_LABELS = { awareness: "Awareness", consideration: "Consideration", conversion: "Conversion", unclassified: "Sin clasificar" };
              if (!by_objective) return null;
              const pieData = Object.entries(by_objective).map(([k, v]) => ({ name: OBJ_LABELS[k] || k, value: v.spend })).filter(d => d.value > 0);
              const total = pieData.reduce((a, d) => a + d.value, 0);
              return (
                <div>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                        {pieData.map((d, i) => <Cell key={i} fill={OBJ_COLORS[d.name] || "#94a3b8"} />)}
                      </Pie>
                      <RTooltip formatter={(v) => "$" + Number(v).toLocaleString()} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-2 justify-center mt-1">
                    {pieData.map(d => (
                      <span key={d.name} className="text-[10px] text-slate-500">
                        <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: OBJ_COLORS[d.name] }} />
                        {d.name} {total > 0 ? Math.round(d.value / total * 100) : 0}%
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Top 10 Bar Chart */}
      {campaigns.length > 0 && (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Top 10 Campanas por Gasto
              <HelpTip text="Las 10 campanas con mayor inversion. Color por objetivo: purpura=awareness, azul=consideration, verde=conversion." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const OBJ_COLORS = { awareness: "#a855f7", consideration: "#3b82f6", conversion: "#10b981", unclassified: "#94a3b8" };
              const top10 = campaigns.slice(0, 10).map(c => ({
                name: c.campaign_name?.length > 35 ? c.campaign_name.substring(0, 35) + "..." : c.campaign_name,
                spend: c.spend,
                fill: OBJ_COLORS[c.objective] || "#94a3b8"
              })).reverse();
              return (
                <ResponsiveContainer width="100%" height={Math.max(250, top10.length * 32)}>
                  <BarChart data={top10} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? "$"+(v/1000).toFixed(0)+"K" : "$"+v} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={200} />
                    <RTooltip formatter={(v) => "$" + Number(v).toLocaleString()} />
                    <Bar dataKey="spend" radius={[0, 4, 4, 0]}>
                      {top10.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Campaigns Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Campanas ({campaigns.length})
            <HelpTip text="Haz clic en los headers para ordenar. Las campanas con mayor gasto aparecen primero por defecto." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-[500px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[28%] sticky top-0 bg-white">Campana</TableHead>
                  <TableHead className="w-[7%] sticky top-0 bg-white">Obj.</TableHead>
                  <TableHead className="w-[7%] sticky top-0 bg-white">Plataf.</TableHead>
                  <SortHeader k="spend" className="text-right w-[9%] sticky top-0 bg-white">Spend</SortHeader>
                  <SortHeader k="spend_share" className="text-right w-[7%] sticky top-0 bg-white">%</SortHeader>
                  <SortHeader k="roas" className="text-right w-[8%] sticky top-0 bg-white">ROAS</SortHeader>
                  <SortHeader k="cpa" className="text-right w-[8%] sticky top-0 bg-white">CPA</SortHeader>
                  <SortHeader k="conversions" className="text-right w-[8%] sticky top-0 bg-white">Conv.</SortHeader>
                  <SortHeader k="ctr" className="text-right w-[7%] sticky top-0 bg-white">CTR</SortHeader>
                  <SortHeader k="cpc" className="text-right w-[7%] sticky top-0 bg-white">CPC</SortHeader>
                  <TableHead className="w-[8%] text-right sticky top-0 bg-white">Dias</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(c => (
                  <TableRow key={c.campaign_id} className="text-xs">
                    <TableCell>
                      <p className="font-medium text-slate-700 truncate max-w-[220px]">{c.campaign_name}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[9px] ${(OBJ_STYLES[c.objective] || OBJ_STYLES.unclassified).color}`}>
                        {(OBJ_STYLES[c.objective] || OBJ_STYLES.unclassified).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[9px] ${PLATFORM_COLORS[c.platform] || "bg-slate-100"}`}>
                        {c.platform?.replace("_ads","").replace("_"," ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmtM(c.spend)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-400">{c.spend_share}%</TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={c.roas >= 3 ? "text-emerald-600 font-bold" : c.roas >= 1 ? "text-amber-600" : "text-red-600"}>{c.roas}x</span>
                    </TableCell>
                    <TableCell className="text-right font-mono">${fmt(c.cpa)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(c.conversions)}</TableCell>
                    <TableCell className="text-right font-mono">{c.ctr}%</TableCell>
                    <TableCell className="text-right font-mono">${fmt(c.cpc)}</TableCell>
                    <TableCell className="text-right text-slate-400">{c.active_days}d</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// === MAIN VIEW ===
export const CampaignPerformanceView = ({ onBack }) => {
  const [view, setView] = React.useState("clients");
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [days, setDays] = React.useState(30);

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Volver</Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-1">
                  Campaign Performance
                  <HelpTip text="Analisis on-the-fly del rendimiento de campanas. Los datos se calculan en tiempo real desde Lakebase sin precalculos." side="bottom" />
                </h1>
                <p className="text-xs text-slate-500">Rendimiento por campana, plataforma y cliente</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {view === "clients" && <ClientList onSelect={(c) => { setSelectedClient(c); setView("dashboard"); }} days={days} setDays={setDays} />}
        {view === "dashboard" && <Dashboard cliente={selectedClient} days={days} setDays={setDays} onBack={() => { setSelectedClient(null); setView("clients"); }} />}
      </main>
    </div>
    </TooltipProvider>
  );
};
